import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  processAction,
  getCaseDetails,
  getItemApptStatuses,
  GetCaseLineItemDetail,
} from 'src/services/WorkflowService'
import {
  getInvoiceTypeCodeOptions,
  getReferralSourceOptions,
  getReferralContactOptions,
  getMedicalProviderOptions,
} from 'src/services/CaseService'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { PatientInvoiceDetailDTO } from 'src/viewModels/workflow/PatientInvoiceDetailDTO'
import { TypeCodeOption } from 'src/viewModels/workflow/TypeCodeOption'
import { ReferralSourceOption } from 'src/viewModels/workflow/ReferralSourceOption'
import { ReferralSourceContactOption } from 'src/viewModels/workflow/ReferralContactOption'
import { MedicalProviderOption } from 'src/viewModels/workflow/MedicalProviderOption'
import { CaseAppointmentStatusOption } from '../../viewModels/workflow/CaseAppointmentStatusOption'
import { Autocomplete } from '@mui/material'
import { formatCurrency } from 'src/utils/Formatter'

interface IPatientInvoiceDetailDTOProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function EditCaseDetailLineItemModal(props: IPatientInvoiceDetailDTOProps) {
  const formikRef = React.useRef<any>(null)
  const [loading, setLoading] = useState(false)
  const [caseDetail, setCaseDetail] = useState<PatientInvoiceDetailDTO | null>(null)
  const [lineItems, setLineItems] = useState<any[]>([])
  const [selectedLineItemId, setSelectedLineItemId] = useState<string>('')
  const [typeCodeOptions, setTypeCodeOptions] = useState<TypeCodeOption[]>([])
  const [referralSourceOptions, setReferralSourceOptions] = useState<
    ReferralSourceOption[]
  >([])
  const [referralSourceContactOptions, setReferralSourceContactOptions] = useState<
    ReferralSourceContactOption[]
  >([])
  const [providerOptions, setProviderOptions] = useState<MedicalProviderOption[]>([])
  const [caseApptStatusOption, setCaseApptStatuses] = useState<
    CaseAppointmentStatusOption[]
  >([])
  const [caseLoading, setCaseLoading] = useState(false)

  useEffect(() => {
    if (props.isOpen && props.associatedItemId) {
      GetCaseLineItemDetail(Number(props.associatedItemId), false)
        .then((items) => setLineItems(items || []))
        .catch(() => setLineItems([]))
    } else if (!props.isOpen) {
      setLineItems([])
      setSelectedLineItemId('')
    }
  }, [props.isOpen, props.associatedItemId])

  useEffect(() => {
    if (props.isOpen && selectedLineItemId) {
      setCaseLoading(true)
      getCaseDetails(Number(selectedLineItemId))
        .then((data) => setCaseDetail(data || null))
        .finally(() => setCaseLoading(false))
    } else if (!selectedLineItemId) {
      setCaseDetail(null)
    }
  }, [props.isOpen, selectedLineItemId])

  useEffect(() => {
    if (caseDetail && selectedLineItemId && !caseLoading) {
      const allFieldsTouched = {
        id: true,
        invoiceNumber: true,
        groupNumber: true,
        dos: true,
        invoiceDate: true,
        dateForRush: true,
        rsId: true,
        medicalProvider: true,
        rscontactId: true,
        typeCode: true,
        providerId: true,
        apptStatus: true,
        numberOfHours: true,
        billingPricePerHour: true,
        invoiceAmount: true,
        invoiceSpecificText: true,
        referralSource: true,
        comments: true,
      }
      formikRef.current?.setTouched(allFieldsTouched)
      setTimeout(() => {
        formikRef.current?.validateForm()
      }, 0)
    }
  }, [caseDetail, selectedLineItemId, caseLoading])

  useEffect(() => {
    const fetchData = async () => {
      const options = await getInvoiceTypeCodeOptions()
      setTypeCodeOptions(options || [])
      const SourceOptions = await getReferralSourceOptions()
      const enabledSources = (SourceOptions || []).filter((opt) => opt.isEnabled)

      const trimmedSources = enabledSources.map((source) => ({
        ...source,
        description: source.description.trim(),
      }))

      const uniqueSources = trimmedSources.filter(
        (source, index, self) =>
          index === self.findIndex((s) => s.description === source.description)
      )

      uniqueSources.sort((a, b) => a.description.localeCompare(b.description))
      setReferralSourceOptions(uniqueSources)

      const ProviderOption = await getMedicalProviderOptions()
      setProviderOptions(ProviderOption || [])
      const caseApptStatusOptions = await getItemApptStatuses()
      setCaseApptStatuses(caseApptStatusOptions || [])
    }
    const preloadReferralContacts = async () => {
      if (caseDetail && caseDetail.rsId) {
        const contacts = await getReferralContactOptions(String(caseDetail.rsId))
        setReferralSourceContactOptions(contacts || [])
      } else {
        setReferralSourceContactOptions([])
      }
    }
    preloadReferralContacts()
    fetchData()
  }, [caseDetail])

  const handlesOnClose = () => {
    props.closeModal()
  }

  const validationSchema = Yup.object({
    invoiceNumber: Yup.number()
      .typeError('Invoice # must be a number')
      .integer('Invoice # must be an integer')
      .max(2147483647, 'Invoice # must be less than 2,147,483,647'),
    dos: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Date of Service must be a valid date', () => {
        const dateInput = document.getElementById('dos') as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-dos-date',
        'Date of Service must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      ),
    invoiceDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Invoice Date must be a valid date', () => {
        const dateInput = document.getElementById(
          'invoiceDate'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-invoice-date',
        'Invoice Date must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      )
      .test(
        'invoice-date-required-with-number',
        'Invoice Date is required when Invoice Number is entered',
        function (value) {
          const invoiceNumber = this.parent.invoiceNumber
          if (invoiceNumber && invoiceNumber !== '') {
            return value !== null && value !== undefined
          }
          return true
        }
      ),
    dateForRush: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Date For Rush must be a valid date', () => {
        const dateInput = document.getElementById(
          'dateForRush'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-rush-date',
        'Date For Rush must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      ),
    typeCode: Yup.string(),
    providerId: Yup.number(),
    numberOfHours: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired()
      .min(0, 'Number of Hours must be greater than or equal to 0'),
    billingPricePerHour: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired()
      .min(0, 'Price Per Hour must be greater than or equal to 0'),
    invoiceAmount: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired(),
    invoiceSpecificText: Yup.string(),
    referralSource: Yup.string(),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  function InvoiceAmountEffect({ values, setFieldValue }: any) {
    React.useEffect(() => {
      const hours = parseFloat(values.numberOfHours) || 0
      const rate = parseFloat(values.billingPricePerHour) || 0
      const amount = hours * rate
      if (
        values.invoiceAmount !== amount ||
        (values.numberOfHours === '' && values.billingPricePerHour === '')
      ) {
        setFieldValue('invoiceAmount', amount)
      }
    }, [
      values.numberOfHours,
      values.billingPricePerHour,
      setFieldValue,
      values.invoiceAmount,
    ])
    return null
  }

  async function processCaseDetailItem(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    const createLineDetailRequest: PatientInvoiceDetailDTO = {
      id: values.id === '' ? undefined : Number(values.id),
      invoiceNumber:
        values.invoiceNumber === '' ? undefined : Number(values.invoiceNumber),
      groupNumber: values.groupNumber === '' ? undefined : Number(values.groupNumber),
      dos: values.dos || undefined,
      invoiceDate: values.invoiceDate || undefined,
      dateForRush: values.dateForRush || undefined,
      rsId: values.rsId === '' ? undefined : Number(values.rsId),
      rscontactId: values.rscontactId === '' ? undefined : Number(values.rscontactId),
      typeCode: values.typeCode || undefined,
      providerId: values.providerId === '' ? undefined : Number(values.providerId),
      apptStatus: values.apptStatus || undefined,
      numberOfHours:
        values.numberOfHours === '' ? undefined : Number(values.numberOfHours),
      billingPricePerHour:
        values.billingPricePerHour === ''
          ? undefined
          : Number(values.billingPricePerHour),
      invoiceAmount:
        values.invoiceAmount === '' ? undefined : Number(values.invoiceAmount),
      invoiceSpecificText: values.invoiceSpecificText || undefined,
    }

    data.lineDetailRequest = createLineDetailRequest

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish!(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading(false)
        props.closeModal()
      })
  }

  const initialValues = caseDetail
    ? {
        id: caseDetail.id || '',
        referralSource:
          referralSourceOptions.find((opt) => String(opt.id) === String(caseDetail.rsId))
            ?.description || '',
        invoiceNumber:
          caseDetail.invoiceNumber !== undefined && caseDetail.invoiceNumber !== null
            ? String(caseDetail.invoiceNumber)
            : '',
        groupNumber: caseDetail.groupNumber || '',
        dos: caseDetail.dos
          ? typeof caseDetail.dos === 'string'
            ? (caseDetail.dos as string).slice(0, 10)
            : new Date(caseDetail.dos as Date).toISOString().slice(0, 10)
          : '',
        invoiceDate: caseDetail.invoiceDate
          ? typeof caseDetail.invoiceDate === 'string'
            ? (caseDetail.invoiceDate as string).slice(0, 10)
            : new Date(caseDetail.invoiceDate as Date).toISOString().slice(0, 10)
          : '',
        dateForRush: caseDetail.dateForRush
          ? typeof caseDetail.dateForRush === 'string'
            ? (caseDetail.dateForRush as string).slice(0, 10)
            : new Date(caseDetail.dateForRush as Date).toISOString().slice(0, 10)
          : '',
        rsId: caseDetail.rsId || '',
        medicalProvider:
          providerOptions.find((opt) => String(opt.id) === String(caseDetail.providerId))
            ?.description || '',
        rscontactId: caseDetail.rscontactId || '',
        typeCode: caseDetail.typeCode || '',
        providerId: caseDetail.providerId || '',
        apptStatus:
          caseApptStatusOption.find(
            (opt) => String(opt.id) === String(caseDetail.apptStatus)
          )?.id || '',
        numberOfHours: caseDetail.numberOfHours || '',
        billingPricePerHour: caseDetail.billingPricePerHour || '',
        invoiceAmount: caseDetail.invoiceAmount || '',
        amountPaid: caseDetail.amountPaid || '',
        invoiceSpecificText: caseDetail.invoiceSpecificText || '',
        comments: '',
        requireComment: props.action.requireComment,
      }
    : {
        id: '',
        invoiceNumber: '',
        groupNumber: '',
        dos: '',
        invoiceDate: '',
        dateForRush: '',
        rsId: '',
        rscontactId: '',
        typeCode: '',
        providerId: '',
        apptStatus: '',
        numberOfHours: '',
        billingPricePerHour: '',
        invoiceAmount: '',
        invoiceSpecificText: '',
        comments: '',
      }
  const isLineItemSelected = !!selectedLineItemId

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {(loading || caseLoading) && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={processCaseDetailItem}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            isValid,
            handleBlur,
            setFieldValue,
          }) => {
            return (
              <>
                <InvoiceAmountEffect values={values} setFieldValue={setFieldValue} />
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={lineItems}
                        getOptionLabel={(item: any) => item.description}
                        value={
                          lineItems.find((item: any) => item.id === selectedLineItemId) ||
                          null
                        }
                        onChange={(_, newValue: any) => {
                          setSelectedLineItemId(newValue ? newValue.id : '')
                          setFieldValue('lineItemSelect', newValue ? newValue.id : '')
                        }}
                        disabled={false}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Select Line Item"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Autocomplete
                        options={typeCodeOptions}
                        getOptionLabel={(option: TypeCodeOption) =>
                          `${option.description} ($${option.pricePerHour} per hour)`
                        }
                        value={
                          typeCodeOptions.find(
                            (opt) => opt.typeCode === values.typeCode
                          ) || null
                        }
                        onChange={(_, newValue: TypeCodeOption | null) => {
                          setFieldValue('typeCode', newValue ? newValue.typeCode : '')
                          setFieldValue(
                            'billingPricePerHour',
                            newValue ? newValue.pricePerHour : ''
                          )
                        }}
                        disabled={!isLineItemSelected}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Type Code"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            inputProps={{
                              ...params.inputProps,
                            }}
                          />
                        )}
                      />
                      {values.typeCode && (
                        <div style={{ marginTop: 4, color: '#888', fontSize: 13 }}>
                          <em>
                            {
                              typeCodeOptions.find(
                                (opt) => opt.typeCode === values.typeCode
                              )?.additionalDescription
                            }
                          </em>
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'dateForRush' }}
                        InputLabelProps={{ shrink: true }}
                        type="date"
                        name="dateForRush"
                        variant="outlined"
                        value={values.dateForRush}
                        onChange={handleChange}
                        label="Date For Rush"
                        fullWidth
                        error={!!(touched.dateForRush && errors.dateForRush)}
                        helperText={(touched.dateForRush && errors.dateForRush) as string}
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Autocomplete
                        options={referralSourceOptions}
                        getOptionLabel={(option: ReferralSourceOption) =>
                          option.description
                        }
                        value={
                          referralSourceOptions.find(
                            (opt) => opt.description === values.referralSource
                          ) || null
                        }
                        onChange={(_, newValue: ReferralSourceOption | null) => {
                          setFieldValue(
                            'referralSource',
                            newValue ? newValue.description : ''
                          )
                          setFieldValue('rsId', newValue ? newValue.id : '')
                          setFieldValue('rscontactId', '')

                          if (newValue && newValue.id) {
                            getReferralContactOptions(newValue.id).then((contacts) => {
                              setReferralSourceContactOptions(contacts || [])
                            })
                          } else {
                            setReferralSourceContactOptions([])
                          }
                        }}
                        disabled={!isLineItemSelected}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Referral Source"
                            variant="outlined"
                            size="small"
                            error={!!(touched.referralSource && errors.referralSource)}
                            helperText={touched.referralSource && errors.referralSource}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Autocomplete
                        options={referralSourceContactOptions}
                        getOptionLabel={(option: ReferralSourceContactOption) =>
                          option.description
                        }
                        value={
                          referralSourceContactOptions.find(
                            (contact) => String(contact.id) === String(values.rscontactId)
                          ) || null
                        }
                        onChange={(_, newValue: ReferralSourceContactOption | null) => {
                          setFieldValue('rscontactId', newValue ? newValue.id : '')
                        }}
                        disabled={!isLineItemSelected}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Referral Contact"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Autocomplete
                        options={providerOptions}
                        getOptionLabel={(option: MedicalProviderOption) =>
                          option.description
                        }
                        value={
                          providerOptions.find(
                            (opt) => opt.description === values.medicalProvider
                          ) || null
                        }
                        onChange={(_, newValue: MedicalProviderOption | null) => {
                          setFieldValue(
                            'medicalProvider',
                            newValue ? newValue.description : ''
                          )
                          setFieldValue('providerId', newValue ? newValue.id : '')
                        }}
                        disabled={!isLineItemSelected}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Medical Provider"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'dos' }}
                        InputLabelProps={{ shrink: true }}
                        type="date"
                        name="dos"
                        variant="outlined"
                        value={values.dos}
                        onChange={handleChange}
                        label="Date of Service"
                        fullWidth
                        error={!!(touched.dos && errors.dos)}
                        helperText={(touched.dos && errors.dos) as string}
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Autocomplete
                        options={caseApptStatusOption}
                        getOptionLabel={(option: CaseAppointmentStatusOption) =>
                          option.name
                        }
                        value={
                          caseApptStatusOption.find(
                            (opt) => String(opt.id) === String(values.apptStatus)
                          ) || null
                        }
                        onChange={(_, newValue: CaseAppointmentStatusOption | null) => {
                          setFieldValue('apptStatus', newValue ? newValue.id : '')
                        }}
                        disabled={!isLineItemSelected}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Status"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                      <div
                        style={{
                          marginTop: 4,
                          color: '#49cc90',
                          fontSize: 13,
                          fontWeight: 'bold',
                        }}
                      >
                        <em>
                          {isLineItemSelected &&
                            (values.amountPaid == 0.0
                              ? `Amount Paid: $0.00`
                              : `Amount Paid: ${
                                  values.amountPaid
                                    ? formatCurrency(String(values.amountPaid))
                                    : ''
                                }`)}
                        </em>
                      </div>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'invoiceNumber' }}
                        InputLabelProps={{ shrink: true }}
                        name="invoiceNumber"
                        variant="outlined"
                        value={values.invoiceNumber}
                        onChange={handleChange}
                        label="Invoice Number"
                        fullWidth
                        error={!!(touched.invoiceNumber && errors.invoiceNumber)}
                        helperText={
                          (touched.invoiceNumber && errors.invoiceNumber) as string
                        }
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'invoiceDate' }}
                        InputLabelProps={{ shrink: true }}
                        type="date"
                        name="invoiceDate"
                        variant="outlined"
                        value={values.invoiceDate}
                        onChange={handleChange}
                        label="Invoice Date"
                        fullWidth
                        error={!!(touched.invoiceDate && errors.invoiceDate)}
                        helperText={(touched.invoiceDate && errors.invoiceDate) as string}
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'numberOfHours' }}
                        InputLabelProps={{ shrink: true }}
                        name="numberOfHours"
                        variant="outlined"
                        value={values.numberOfHours}
                        onChange={handleChange}
                        label="Number of Hours"
                        fullWidth
                        error={!!(touched.numberOfHours && errors.numberOfHours)}
                        helperText={
                          (touched.numberOfHours && errors.numberOfHours) as string
                        }
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'billingPricePerHour' }}
                        InputLabelProps={{ shrink: true }}
                        name="billingPricePerHour"
                        variant="outlined"
                        value={values.billingPricePerHour}
                        onChange={handleChange}
                        label="Price Per Hour"
                        fullWidth
                        error={
                          !!(touched.billingPricePerHour && errors.billingPricePerHour)
                        }
                        helperText={
                          (touched.billingPricePerHour &&
                            errors.billingPricePerHour) as string
                        }
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'invoiceAmount' }}
                        InputLabelProps={{ shrink: true }}
                        name="invoiceAmount"
                        variant="outlined"
                        value={
                          values.invoiceAmount === '' || values.invoiceAmount === null
                            ? ''
                            : String(values.invoiceAmount)
                        }
                        onChange={handleChange}
                        label="Invoice Amount"
                        fullWidth
                        disabled
                        error={!!(touched.invoiceAmount && errors.invoiceAmount)}
                        helperText={
                          (touched.invoiceAmount && errors.invoiceAmount) as string
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'invoiceSpecificText', maxLength: 50 }}
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        name="invoiceSpecificText"
                        variant="outlined"
                        value={values.invoiceSpecificText}
                        onChange={handleChange}
                        label="Invoice Notes"
                        fullWidth
                        error={
                          !!(touched.invoiceSpecificText && errors.invoiceSpecificText)
                        }
                        helperText={
                          (touched.invoiceSpecificText &&
                            errors.invoiceSpecificText) as string
                        }
                        disabled={!isLineItemSelected}
                      />
                    </Grid>
                    {props.action.displayComment && (
                      <Grid item xs={12}>
                        <TextField
                          onBlur={handleBlur}
                          inputProps={{ id: 'comments' }}
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          name="comments"
                          variant="outlined"
                          value={values.comments}
                          onChange={handleChange}
                          label="Comments"
                          fullWidth
                          multiline
                          minRows={4}
                          required={props.action.requireComment}
                          error={!!(touched.comments && errors.comments)}
                          helperText={(touched.comments && errors.comments) as string}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <DialogActions>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={loading || !isValid}
                    >
                      Execute
                    </Button>
                    <Button onClick={handlesOnClose} color="primary">
                      Close
                    </Button>
                  </DialogActions>
                </Form>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditCaseDetailLineItemModal
